import type { Type } from '@angular/core';

export interface LazyComponentRegistryItem {
  // The name of the lazy Component
  name: LazyComponentId;
  // if true, Vue (runtime) gets lazy loaded before loading the component
  requiresVue?: boolean;

  // function wrapper to return the result of webpack's `import()` function
  path<T = unknown>(this: void): Promise<Type<T>>;
}

/**
 * List of IDs of all registered lazy components
 * [!] NOTICE:
 * The ID's string value has to exactly match the respective Component's name
 */
export enum LazyComponentId {
  Acknowledgement = 'AcknowledgementComponent',
  AppPlaceholder = 'AppPlaceholderComponent',
  BuildToOrder = 'BuildToOrderModalComponent',
  CompareModal = 'CompareModalComponent',
  CompareTeaser = 'CompareTeaserComponent',
  ComparisonMatrixDetailModal = 'ComparisonMatrixDetailModalComponent',
  ConsentSystem = 'ConsentSystemComponent', // Note: this component is about Usercentrics CMP v2 (only)
  ContactDealer = 'ContactDealerModalComponent',
  DealerLocator = 'DealerLocatorComponent',
  DetailedEquipmentModal = 'DetailedEquipmentModalComponent',
  DevLayer = 'DevLayerComponent',
  EnergyConsumptionLabelDisplay = 'EnergyConsumptionLabelDisplayComponent',
  EnergyConsumptionLabelModal = 'EnergyConsumptionLabelModalComponent',
  ExitIntentModal = 'ExitIntentModalComponent',
  FeasibilityConflictModal = 'FeasibilityConflictModalComponent',
  FooterRetailerModal = 'FooterRetailerModalComponent',
  NedcDetailsModal = 'NedcDetailsModalComponent',
  OnlineCodeModal = 'OnlineCodeModalComponent',
  PermanentSummaryModal = 'PermanentSummaryModalComponent',
  PriceDisplay = 'PriceDisplayComponent',
  SaveConfigModal = 'SaveConfigModalComponent',
  ScreenMirroring = 'ScreenMirroringComponent',
  SelectablesSearch = 'SelectablesSearchComponent',
  UpsellingRecommendation = 'UpsellingRecommendationComponent',
  GalleryModal = 'GalleryModalComponent'
}

/**
 * Simple array collection of all registered lazy components
 */
const lazyComponents: LazyComponentRegistryItem[] = [
  {
    name: LazyComponentId.Acknowledgement,
    path: async () => import('src/app/shared/acknowledgement/acknowledgement.component')
  },
  {
    name: LazyComponentId.AppPlaceholder,
    path: async () => import('src/app/shared/app-placeholder/app-placeholder.component')
  },
  {
    name: LazyComponentId.BuildToOrder,
    requiresVue: true,
    path: async () => import('src/app/shared/build-to-order/build-to-order-modal/build-to-order-modal.component')
  },
  {
    name: LazyComponentId.ComparisonMatrixDetailModal,
    path: async () => import('src/app/shared/comparison-matrix-detail-modal/comparison-matrix-detail-modal.component')
  },
  {
    name: LazyComponentId.CompareModal,
    path: async () => import('src/app/features/configuration/compare-modal/compare-modal.component')
  },
  {
    name: LazyComponentId.CompareTeaser,
    path: async () => import('src/app/features/configuration/compare-teaser/compare-teaser.component')
  },
  {
    name: LazyComponentId.ConsentSystem,
    path: async () => import('src/app/shared/consent-system/consent-system.component')
  },
  {
    name: LazyComponentId.ContactDealer,
    requiresVue: true,
    path: async () => import('src/app/shared/contact-dealer/contact-dealer-modal/contact-dealer-modal.component')
  },
  {
    name: LazyComponentId.DealerLocator,
    path: async () => import('src/app/shared/dealer-locator/dealer-locator.component')
  },
  {
    name: LazyComponentId.DevLayer,
    path: async () => import('src/app/core/dev/dev-layer/dev-layer.component')
  },
  {
    name: LazyComponentId.EnergyConsumptionLabelDisplay,
    path: async () =>
      import(
        'src/app/shared/energy-consumption-label/energy-consumption-label-display/energy-consumption-label-display.component'
      )
  },
  {
    name: LazyComponentId.EnergyConsumptionLabelModal,
    path: async () =>
      import(
        'src/app/shared/energy-consumption-label/energy-consumption-label-modal/energy-consumption-label-modal.component'
      )
  },
  {
    name: LazyComponentId.DetailedEquipmentModal,
    path: async () =>
      import('src/app/shared/detailed-equipment/detailed-equipment-modal/detailed-equipment-modal.component')
  },
  {
    name: LazyComponentId.FeasibilityConflictModal,
    path: async () => import('src/app/shared/feasibility-conflict-modal/feasibility-conflict-modal.component')
  },
  {
    name: LazyComponentId.FooterRetailerModal,
    path: async () => import('src/app/shared/footer-retailer-modal/footer-retailer-modal.component')
  },
  {
    name: LazyComponentId.NedcDetailsModal,
    path: async () => import('src/app/shared/nedc-details-modal/nedc-details-modal.component')
  },
  {
    name: LazyComponentId.OnlineCodeModal,
    path: async () => import('src/app/shared/online-code/online-code-modal/online-code-modal.component')
  },
  {
    name: LazyComponentId.PermanentSummaryModal,
    path: async () => import('src/app/shared/permanent-summary-modal/permanent-summary-modal.component')
  },
  {
    name: LazyComponentId.PriceDisplay,
    path: async () => import('src/app/shared/price-display/price-display.component')
  },
  {
    name: LazyComponentId.SaveConfigModal,
    path: async () => import('src/app/shared/save-config-modal/save-config-modal.component')
  },
  {
    name: LazyComponentId.ScreenMirroring,
    path: async () => import('src/app/shared/screen-mirroring/screen-mirroring.component')
  },
  {
    name: LazyComponentId.SelectablesSearch,
    path: async () => import('src/app/shared/selectables-search/selectables-search.component')
  },
  {
    name: LazyComponentId.UpsellingRecommendation,
    requiresVue: true,
    path: async () =>
      import('src/app/features/configuration/upselling-recommendatation/upselling-recommendation.component')
  },
  {
    name: LazyComponentId.GalleryModal,
    path: async () => import('src/app/shared/gallery-modal/gallery-modal.component')
  }
] as unknown as LazyComponentRegistryItem[];

class LazyComponentsRegistry {
  private readonly map: Map<LazyComponentId, LazyComponentRegistryItem>;

  public constructor(array: LazyComponentRegistryItem[]) {
    this.map = new Map(array.map((item: LazyComponentRegistryItem) => [item.name, item]));
  }

  public get(id: LazyComponentId): LazyComponentRegistryItem {
    const item = this.map.get(id);
    if (!item) throw new Error(`Lazy component with ID '${id}' not found`);
    return item;
  }

  public has(id: LazyComponentId): boolean {
    return this.map.has(id);
  }
}

/**
 * Generated map of registered lazy modules created from given array
 */
export const lazyComponentsRegistry = new LazyComponentsRegistry(lazyComponents);
